import React, { useEffect, useState } from 'react';
import {
  Search,
  Filter,
  MapPin,
  Tag,
  Star,
  ChevronDown,
  ChevronUp,
  X,
  Leaf,
  Users,
  Truck
} from 'lucide-react';

const CATEGORIES = [
  { id: 'farm', label: 'Farms' },
  { id: 'market', label: 'Farmers Markets' },
  { id: 'csa', label: 'CSA Programs' },
  { id: 'upick', label: 'U-Pick' },
  { id: 'restaurant', label: 'Restaurants' },
  { id: 'specialty', label: 'Specialty Shops' }
];

const PRODUCTS = [
  { id: 'produce', label: 'Produce' },
  { id: 'eggs', label: 'Eggs' },
  { id: 'honey', label: 'Honey' },
  { id: 'bakery', label: 'Bakery' },
  { id: 'flowers', label: 'Flowers' },
  { id: 'coffee', label: 'Coffee' }
];

const DISTANCES = [
  { id: '5', label: '5 miles' },
  { id: '10', label: '10 miles' },
  { id: '25', label: '25 miles' },
  { id: '50', label: '50 miles' },
  { id: 'any', label: 'Any distance' }
];

const FEATURES = [
  { id: 'organic', label: 'Organic', icon: Leaf },
  { id: 'family-owned', label: 'Family-Owned', icon: Users },
  { id: 'delivery', label: 'Delivery Available', icon: Truck }
];

const FilterSidebar = ({
  filters = {},
  onFilterChange,
  onClear,
  onClearFilters,
  isMobile = false,
  compact = false
}) => {
  const [expandedSections, setExpandedSections] = useState({
    search: true,
    categories: true,
    products: true,
    distance: true,
    features: true
  });

  const [searchValue, setSearchValue] = useState(filters.search || '');

  useEffect(() => {
    setSearchValue(filters.search || '');
  }, [filters.search]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const emitFilters = (nextFilters) => {
    if (!onFilterChange) return;
    onFilterChange(nextFilters);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    emitFilters({ ...filters, search: value });
  };

  const handleCheckboxChange = (filterType, value) => {
    const currentValues = filters[filterType] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    emitFilters({ ...filters, [filterType]: newValues });
  };

  const handleRadioChange = (filterType, value) => {
    emitFilters({ ...filters, [filterType]: value });
  };

  const isChecked = (filterType, value) => {
    const values = filters[filterType] || [];
    return values.includes(value);
  };

  const hasActiveFilters = () => {
    return (
      (filters.search && filters.search.length > 0) ||
      (filters.categories && filters.categories.length > 0) ||
      (filters.products && filters.products.length > 0) ||
      (filters.distance && filters.distance !== 'any') ||
      (filters.features && filters.features.length > 0)
    );
  };

  const handleClear = () => {
    if (onClearFilters) return onClearFilters();
    if (onClear) return onClear();
    emitFilters({
      ...filters,
      search: '',
      categories: [],
      products: [],
      distance: 'any',
      features: []
    });
  };

  const styles = {
    container: {
      backgroundColor: 'var(--thm-bg-cream, #FDF8F3)',
      borderRadius: '12px',
      padding: isMobile || compact ? '16px' : '24px',
      position: isMobile ? 'relative' : 'sticky',
      top: isMobile ? 'auto' : '24px',
      maxHeight: isMobile ? 'none' : 'calc(100vh - 48px)',
      overflowY: 'auto'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '20px',
      paddingBottom: '16px',
      borderBottom: '1px solid var(--thm-bg-sage, #E8EDE5)'
    },
    headerTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '18px',
      fontWeight: '700',
      color: 'var(--thm-black, #2C3E2D)',
      fontFamily: 'var(--thm-font, serif)',
      margin: 0
    },
    section: {
      marginBottom: '20px'
    },
    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      padding: '12px 0',
      borderBottom: '1px solid var(--thm-bg-sage, #E8EDE5)'
    },
    sectionTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '16px',
      fontWeight: '600',
      color: 'var(--thm-black, #2C3E2D)',
      fontFamily: 'var(--thm-font-2, sans-serif)',
      margin: 0
    },
    sectionContent: {
      paddingTop: '16px'
    },
    searchWrapper: {
      position: 'relative',
      marginBottom: '8px'
    },
    searchInput: {
      width: '100%',
      padding: '12px 16px 12px 44px',
      border: '1px solid var(--thm-bg-sage, #E8EDE5)',
      borderRadius: '8px',
      fontSize: '15px',
      fontFamily: 'var(--thm-font-2, sans-serif)',
      backgroundColor: '#ffffff',
      color: 'var(--thm-black, #2C3E2D)',
      outline: 'none',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
    },
    searchIcon: {
      position: 'absolute',
      left: '14px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: 'var(--thm-gray, #5D6B5E)'
    },
    checkboxGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px'
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      cursor: 'pointer',
      fontSize: '15px',
      fontFamily: 'var(--thm-font-2, sans-serif)',
      color: 'var(--thm-gray, #5D6B5E)',
      transition: 'color 0.2s ease'
    },
    checkbox: {
      position: 'relative',
      width: '20px',
      height: '20px',
      flexShrink: 0
    },
    checkboxInput: {
      position: 'absolute',
      opacity: 0,
      width: '100%',
      height: '100%',
      cursor: 'pointer',
      margin: 0
    },
    checkboxCustom: (checked) => ({
      position: 'absolute',
      top: 0,
      left: 0,
      width: '20px',
      height: '20px',
      backgroundColor: checked ? 'var(--thm-base, #2D5A27)' : '#ffffff',
      border: `2px solid ${checked ? 'var(--thm-base, #2D5A27)' : 'var(--thm-bg-sage, #E8EDE5)'}`,
      borderRadius: '4px',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'none'
    }),
    checkmark: {
      width: '10px',
      height: '10px',
      color: '#ffffff'
    },
    radioGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px'
    },
    radioLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      cursor: 'pointer',
      fontSize: '15px',
      fontFamily: 'var(--thm-font-2, sans-serif)',
      color: 'var(--thm-gray, #5D6B5E)',
      transition: 'color 0.2s ease'
    },
    radio: {
      position: 'relative',
      width: '20px',
      height: '20px',
      flexShrink: 0
    },
    radioInput: {
      position: 'absolute',
      opacity: 0,
      width: '100%',
      height: '100%',
      cursor: 'pointer',
      margin: 0
    },
    radioCustom: (checked) => ({
      position: 'absolute',
      top: 0,
      left: 0,
      width: '20px',
      height: '20px',
      backgroundColor: '#ffffff',
      border: `2px solid ${checked ? 'var(--thm-base, #2D5A27)' : 'var(--thm-bg-sage, #E8EDE5)'}`,
      borderRadius: '50%',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'none'
    }),
    radioDot: {
      width: '10px',
      height: '10px',
      backgroundColor: 'var(--thm-base, #2D5A27)',
      borderRadius: '50%'
    },
    featureLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      cursor: 'pointer',
      fontSize: '15px',
      fontFamily: 'var(--thm-font-2, sans-serif)',
      color: 'var(--thm-gray, #5D6B5E)',
      transition: 'color 0.2s ease'
    },
    featureIcon: {
      color: 'var(--thm-base, #2D5A27)',
      opacity: 0.7
    },
    buttonContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      marginTop: '24px',
      paddingTop: '20px',
      borderTop: '1px solid var(--thm-bg-sage, #E8EDE5)'
    },
    clearButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '12px 20px',
      backgroundColor: 'transparent',
      border: '1px solid var(--thm-bg-sage, #E8EDE5)',
      borderRadius: '8px',
      fontSize: '15px',
      fontWeight: '500',
      fontFamily: 'var(--thm-font-2, sans-serif)',
      color: 'var(--thm-gray, #5D6B5E)',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    applyButton: {
      display: isMobile ? 'flex' : 'none',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '14px 20px',
      backgroundColor: 'var(--thm-base, #2D5A27)',
      border: 'none',
      borderRadius: '8px',
      fontSize: '15px',
      fontWeight: '600',
      fontFamily: 'var(--thm-font-2, sans-serif)',
      color: '#ffffff',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    chevron: {
      color: 'var(--thm-gray, #5D6B5E)',
      transition: 'transform 0.2s ease'
    }
  };

  const renderCheckbox = (id, label, filterType, IconComponent = null) => {
    const checked = isChecked(filterType, id);
    return (
      <label key={id} style={IconComponent ? styles.featureLabel : styles.checkboxLabel}>
        <span style={styles.checkbox}>
          <input
            type="checkbox"
            style={styles.checkboxInput}
            checked={checked}
            onChange={() => handleCheckboxChange(filterType, id)}
          />
          <span style={styles.checkboxCustom(checked)}>
            {checked && (
              <svg
                style={styles.checkmark}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            )}
          </span>
        </span>
        {IconComponent && <IconComponent size={16} style={styles.featureIcon} />}
        <span>{label}</span>
      </label>
    );
  };

  const renderRadio = (id, label, filterType) => {
    const checked = filters[filterType] === id;
    return (
      <label key={id} style={styles.radioLabel}>
        <span style={styles.radio}>
          <input
            type="radio"
            name={filterType}
            style={styles.radioInput}
            checked={checked}
            onChange={() => handleRadioChange(filterType, id)}
          />
          <span style={styles.radioCustom(checked)}>
            {checked && <span style={styles.radioDot}></span>}
          </span>
        </span>
        <span>{label}</span>
      </label>
    );
  };

  const renderSection = (id, title, IconComponent, content) => {
    const isExpanded = expandedSections[id];
    return (
      <div style={styles.section}>
        <div
          style={styles.sectionHeader}
          onClick={() => toggleSection(id)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && toggleSection(id)}
        >
          <h4 style={styles.sectionTitle}>
            <IconComponent size={18} style={{ color: 'var(--thm-base, #2D5A27)' }} />
            {title}
          </h4>
          {isExpanded ? (
            <ChevronUp size={18} style={styles.chevron} />
          ) : (
            <ChevronDown size={18} style={styles.chevron} />
          )}
        </div>
        {isExpanded && (
          <div style={styles.sectionContent}>
            {content}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h3 style={styles.headerTitle}>
          <Filter size={20} style={{ color: 'var(--thm-base, #2D5A27)' }} />
          Filter Listings
        </h3>
      </div>

      {/* Search Section */}
      <div style={styles.section}>
        <div style={styles.searchWrapper}>
          <Search size={18} style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search farms, products..."
            style={styles.searchInput}
            value={searchValue}
            onChange={handleSearchChange}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--thm-base, #2D5A27)';
              e.target.style.boxShadow = '0 0 0 3px rgba(45, 90, 39, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--thm-bg-sage, #E8EDE5)';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
      </div>

      {/* Categories Section */}
      {renderSection(
        'categories',
        'Categories',
        Filter,
        <div style={styles.checkboxGroup}>
          {CATEGORIES.map(cat => renderCheckbox(cat.id, cat.label, 'categories'))}
        </div>
      )}

      {/* Products Section */}
      {renderSection(
        'products',
        'Products',
        Tag,
        <div style={styles.checkboxGroup}>
          {PRODUCTS.map(prod => renderCheckbox(prod.id, prod.label, 'products'))}
        </div>
      )}

      {/* Distance Section */}
      {renderSection(
        'distance',
        'Distance',
        MapPin,
        <div style={styles.radioGroup}>
          {DISTANCES.map(dist => renderRadio(dist.id, dist.label, 'distance'))}
        </div>
      )}

      {/* Features Section */}
      {renderSection(
        'features',
        'Features',
        Star,
        <div style={styles.checkboxGroup}>
          {FEATURES.map(feat => renderCheckbox(feat.id, feat.label, 'features', feat.icon))}
        </div>
      )}

      {/* Action Buttons */}
      <div style={styles.buttonContainer}>
        {hasActiveFilters() && (
          <button
            style={styles.clearButton}
            onClick={handleClear}
            onMouseEnter={(e) => {
              e.target.style.borderColor = 'var(--thm-base, #2D5A27)';
              e.target.style.color = 'var(--thm-base, #2D5A27)';
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = 'var(--thm-bg-sage, #E8EDE5)';
              e.target.style.color = 'var(--thm-gray, #5D6B5E)';
            }}
          >
            <X size={16} />
            Clear All Filters
          </button>
        )}

        {isMobile && (
          <button
            style={styles.applyButton}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'var(--thm-base-light, #4A7C43)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'var(--thm-base, #2D5A27)';
            }}
          >
            Apply Filters
          </button>
        )}
      </div>
    </aside>
  );
};

export default FilterSidebar;
